import { useMemo, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "src/lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Skeleton } from "src/components/ui/skeleton";
import EmptyState from "./EmptyState";

export interface Column<T> {
	/** Stable key, also used as the React key for the cell. */
	key: string;
	header: ReactNode;
	cell: (row: T, index: number) => ReactNode;
	/** Extra classes for both the header and body cells of this column. */
	className?: string;
	/** Marks the column used as the heading of the stacked mobile card. */
	primary?: boolean;
	/** Omit this column from the stacked mobile card (e.g. a row-actions kebab). */
	hideOnMobile?: boolean;
}

export type RowsPerPageOption = number | "All";

interface DataTableProps<T> {
	data: T[];
	/**
	 * Server-side paging. When set, `data` is treated as the current page only and the
	 * component stops slicing — the caller owns `page`/`rowsPerPage` and refetches.
	 */
	totalRows?: number;
	page?: number;
	onPageChange?: (page: number) => void;
	rowsPerPage?: RowsPerPageOption;
	onRowsPerPageChange?: (rowsPerPage: RowsPerPageOption) => void;
	columns: Column<T>[];
	/** Stable row identity; falls back to the array index. */
	rowKey?: (row: T, index: number) => string | number;
	onRowClick?: (row: T) => void;
	loading?: boolean;
	emptyTitle?: string;
	emptyDescription?: ReactNode;
	emptyAction?: ReactNode;
	rowsPerPageOptions?: RowsPerPageOption[];
	defaultRowsPerPage?: RowsPerPageOption;
	className?: string;
	/** Rendered in the footer beside the rows-per-page control. */
	footerNote?: ReactNode;
}

const skeletonRows = 5;

/**
 * Prototype table pattern: a bordered card, a rows-per-page footer, and a first-class
 * empty state.
 *
 * Paging has two modes. By default it slices client-side, like the prototype. Pass
 * `totalRows` (plus `page`/`onPageChange`) to hand paging to the server instead — spec §1.2
 * calls for real offset/limit paging, which the customers/invoices endpoints already support.
 *
 * Below `md` the table is replaced by stacked cards (responsive convention, D3).
 */
function DataTable<T>({
	data,
	totalRows,
	page: controlledPage,
	onPageChange,
	rowsPerPage: controlledRowsPerPage,
	onRowsPerPageChange,
	columns,
	rowKey,
	onRowClick,
	loading = false,
	emptyTitle = "Nothing here yet",
	emptyDescription,
	emptyAction,
	rowsPerPageOptions = [10, 25, "All"],
	defaultRowsPerPage = 10,
	className,
	footerNote,
}: DataTableProps<T>) {
	const isServerPaged = totalRows !== undefined;

	const [uncontrolledRowsPerPage, setUncontrolledRowsPerPage] = useState<RowsPerPageOption>(defaultRowsPerPage);
	const [uncontrolledPage, setUncontrolledPage] = useState(0);

	const rowsPerPage = controlledRowsPerPage ?? uncontrolledRowsPerPage;
	const setRowsPerPage = (next: RowsPerPageOption) => {
		onRowsPerPageChange ? onRowsPerPageChange(next) : setUncontrolledRowsPerPage(next);
	};

	const pageSize = rowsPerPage === "All" ? (isServerPaged ? totalRows : data.length) || 1 : rowsPerPage;
	const rowCount = isServerPaged ? totalRows : data.length;
	const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));

	// Controlled page is 1-based (it maps to an API query param); internal state is 0-based.
	const currentPage = isServerPaged
		? Math.min(Math.max((controlledPage ?? 1) - 1, 0), pageCount - 1)
		: Math.min(uncontrolledPage, pageCount - 1);

	const goToPage = (next: number) => {
		if (onPageChange) onPageChange(next + 1);
		else setUncontrolledPage(next);
	};

	const pageRows = useMemo(
		() =>
			isServerPaged || rowsPerPage === "All"
				? data
				: data.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
		[data, isServerPaged, rowsPerPage, currentPage, pageSize]
	);

	const keyOf = (row: T, index: number) => rowKey?.(row, index) ?? index;
	const primaryColumn = columns.find((column) => column.primary) ?? columns[0];

	const showEmpty = !loading && data.length === 0;

	return (
		<div className={cn("tw-rounded-lg tw-border tw-border-border tw-bg-white tw-shadow-card", className)}>
			{showEmpty ? (
				<EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
			) : (
				<>
					{/* Desktop table */}
					<div className="tw-hidden md:tw-block">
						<Table>
							<TableHeader>
								<TableRow className="hover:tw-bg-transparent">
									{columns.map((column) => (
										<TableHead key={column.key} className={column.className}>
											{column.header}
										</TableHead>
									))}
								</TableRow>
							</TableHeader>
							<TableBody>
								{loading
									? Array.from({ length: skeletonRows }).map((_, rowIndex) => (
											<TableRow key={`skeleton-${rowIndex}`} className="hover:tw-bg-transparent">
												{columns.map((column) => (
													<TableCell key={column.key}>
														<Skeleton className="tw-h-4 tw-w-full tw-max-w-[140px]" />
													</TableCell>
												))}
											</TableRow>
										))
									: pageRows.map((row, rowIndex) => (
											<TableRow
												key={keyOf(row, rowIndex)}
												onClick={onRowClick ? () => onRowClick(row) : undefined}
												className={cn(onRowClick && "tw-cursor-pointer")}
											>
												{columns.map((column) => (
													<TableCell key={column.key} className={column.className}>
														{column.cell(row, rowIndex)}
													</TableCell>
												))}
											</TableRow>
										))}
							</TableBody>
						</Table>
					</div>

					{/* Mobile stacked cards */}
					<div className="tw-divide-y tw-divide-border md:tw-hidden">
						{loading
							? Array.from({ length: 3 }).map((_, rowIndex) => (
									<div key={`skeleton-card-${rowIndex}`} className="tw-space-y-2 tw-p-4">
										<Skeleton className="tw-h-4 tw-w-1/2" />
										<Skeleton className="tw-h-3 tw-w-2/3" />
									</div>
								))
							: pageRows.map((row, rowIndex) => (
									<div
										key={keyOf(row, rowIndex)}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										className={cn("tw-space-y-2 tw-p-4", onRowClick && "tw-cursor-pointer active:tw-bg-bg")}
									>
										<div className="tw-text-[14px] tw-font-semibold tw-text-text">
											{primaryColumn?.cell(row, rowIndex)}
										</div>
										<dl className="tw-space-y-1.5">
											{columns
												.filter((column) => column !== primaryColumn && !column.hideOnMobile)
												.map((column) => (
													<div key={column.key} className="tw-flex tw-items-center tw-justify-between tw-gap-3">
														<dt className="tw-text-[12px] tw-text-text-soft">{column.header}</dt>
														<dd className="tw-text-right tw-text-[13px] tw-text-text">
															{column.cell(row, rowIndex)}
														</dd>
													</div>
												))}
										</dl>
									</div>
								))}
					</div>

					{/* Footer: rows per page + paging */}
					<div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-t tw-border-border tw-px-4 tw-py-3.5 tw-text-[12.5px] tw-text-text-soft sm:tw-px-5">
						<label className="tw-flex tw-items-center tw-gap-2">
							<span>Rows per page</span>
							<select
								value={String(rowsPerPage)}
								onChange={(event) => {
									const next = event.target.value;
									setRowsPerPage(next === "All" ? "All" : Number(next));
									goToPage(0);
								}}
								className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-px-2.5 tw-py-1.5 tw-text-[12.5px] tw-text-text focus:tw-border-teal focus:tw-outline-none"
							>
								{rowsPerPageOptions.map((option) => (
									<option key={String(option)} value={String(option)}>
										{option}
									</option>
								))}
							</select>
						</label>

						{footerNote && <div className="tw-order-last tw-w-full sm:tw-order-none sm:tw-w-auto">{footerNote}</div>}

						{pageCount > 1 && (
							<div className="tw-ml-auto tw-flex tw-items-center tw-gap-1">
								<span className="tw-mr-2">
									Page {currentPage + 1} of {pageCount}
								</span>
								<button
									type="button"
									aria-label="Previous page"
									disabled={currentPage === 0}
									onClick={() => goToPage(Math.max(0, currentPage - 1))}
									className="tw-rounded-lg tw-border tw-border-border tw-p-1.5 tw-transition-colors hover:tw-bg-bg disabled:tw-opacity-40 disabled:hover:tw-bg-transparent"
								>
									<ChevronLeft className="tw-h-4 tw-w-4" />
								</button>
								<button
									type="button"
									aria-label="Next page"
									disabled={currentPage >= pageCount - 1}
									onClick={() => goToPage(Math.min(pageCount - 1, currentPage + 1))}
									className="tw-rounded-lg tw-border tw-border-border tw-p-1.5 tw-transition-colors hover:tw-bg-bg disabled:tw-opacity-40 disabled:hover:tw-bg-transparent"
								>
									<ChevronRight className="tw-h-4 tw-w-4" />
								</button>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}

export default DataTable;
